/**
 * Opportunity React Query Hooks (shared)
 *
 * Core hooks usable by any app. Pipeline-specific hooks that depend on
 * admin-config stay in the Sales package.
 */

import {
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { opportunityApi } from '../api/opportunityApi';
import type { OpportunityClose, OppSalesTeamMember, RatingChange } from '../api/opportunityApi';
import type { Opportunity, OpportunityQueryParams } from './types';

export const opportunityKeys = {
  all: ['opportunities'] as const,
  lists: () => [...opportunityKeys.all, 'list'] as const,
  list: (params: OpportunityQueryParams) => [...opportunityKeys.lists(), params] as const,
  details: () => [...opportunityKeys.all, 'detail'] as const,
  detail: (id: string) => [...opportunityKeys.details(), id] as const,
  won: (params?: Omit<OpportunityQueryParams, 'stateCode'>) =>
    [...opportunityKeys.all, 'won', params] as const,
  open: (params?: Omit<OpportunityQueryParams, 'stateCode'>) =>
    [...opportunityKeys.all, 'open', params] as const,
  pipeline: (params?: Omit<OpportunityQueryParams, 'stateCode' | 'stages'>) =>
    [...opportunityKeys.all, 'pipeline', params] as const,
  lost: (params?: Omit<OpportunityQueryParams, 'stateCode'>) =>
    [...opportunityKeys.all, 'lost', params] as const,
  close: (oppId: string) => [...opportunityKeys.all, 'close', oppId] as const,
  ratingHistory: (oppId: string) => [...opportunityKeys.all, 'ratingHistory', oppId] as const,
  salesTeam: (oppId: string) => [...opportunityKeys.all, 'salesTeam', oppId] as const,
};

export function useOpportunities(
  params: OpportunityQueryParams = {},
  options?: Omit<UseQueryOptions<Opportunity[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: opportunityKeys.list(params),
    queryFn: () => opportunityApi.list(params),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useOpportunity(
  id: string,
  options?: Omit<UseQueryOptions<Opportunity, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: opportunityKeys.detail(id),
    queryFn: () => opportunityApi.get(id),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useWonOpportunities(
  params: Omit<OpportunityQueryParams, 'stateCode'> = {},
  options?: Omit<UseQueryOptions<Opportunity[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: opportunityKeys.won(params),
    queryFn: () => opportunityApi.getWon(params),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useOpenOpportunities(
  params: Omit<OpportunityQueryParams, 'stateCode'> = {},
  options?: Omit<UseQueryOptions<Opportunity[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: opportunityKeys.open(params),
    queryFn: () => opportunityApi.getOpen(params),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * The resolved close record for one opportunity (the #1449 rule, see
 * resolveCloseRecord). Pass the opportunity's `actualCloseDate` when you have
 * it: it anchors resolution when a reopened deal carries more than one close.
 */
export function useOpportunityClose(
  opportunityId: string,
  actualCloseDate?: string,
  options?: Omit<UseQueryOptions<OpportunityClose | null, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: [...opportunityKeys.close(opportunityId), actualCloseDate ?? ''] as const,
    queryFn: () => opportunityApi.getClose(opportunityId, actualCloseDate),
    enabled: Boolean(opportunityId),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Rating-change history for one opportunity (Dataverse audit). Lazy: only
 * fetched when `opportunityId` is set (e.g. when a detail dialog opens), so
 * the per-record RetrieveRecordChangeHistory call isn't made for every row.
 */
export function useOppRatingHistory(
  opportunityId: string | null | undefined,
  options?: Omit<UseQueryOptions<RatingChange[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: opportunityKeys.ratingHistory(opportunityId ?? ''),
    queryFn: () => opportunityApi.getRatingHistory(opportunityId!),
    enabled: Boolean(opportunityId),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Users on the opportunity's Dynamics "Sales Team" subgrid (connection rows).
 * Read-only — Vera never writes these; the team is managed in Dynamics.
 */
export function useOpportunitySalesTeam(
  opportunityId: string,
  options?: Omit<UseQueryOptions<OppSalesTeamMember[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: opportunityKeys.salesTeam(opportunityId),
    queryFn: () => opportunityApi.getSalesTeam(opportunityId),
    enabled: Boolean(opportunityId),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch close reasons for all lost opportunities in a single bulk query.
 * Returns a Map from opportunityId → formatted close reason string.
 */
export function useCloseReasons() {
  return useQuery({
    queryKey: [...opportunityKeys.all, 'closeReasons'] as const,
    queryFn: () => opportunityApi.getCloseReasons(),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Products Sold for every closed opportunity in one bulk query, keyed by
 * opportunityId. Use this on LIST pages: the per-opportunity useOpportunityClose
 * would issue one request per row.
 */
export function useProductsSold() {
  return useQuery({
    queryKey: [...opportunityKeys.all, 'productsSold'] as const,
    queryFn: () => opportunityApi.getProductsSold(),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Every Lost opportunity (statecode 2). Includes renewals; the caller filters.
 */
export function useLostOpportunities(
  params: Omit<OpportunityQueryParams, 'stateCode'> = {},
) {
  return useQuery({
    queryKey: opportunityKeys.lost(params),
    queryFn: () => opportunityApi.getLost(params),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Every close activity grouped by opportunityId, in one bulk query. Pair with
 * `resolveCloseRecord` to pick the right record per opportunity. Use this on
 * LIST pages: the per-opportunity useOpportunityClose would issue one request
 * per row.
 */
export function useAllCloses() {
  return useQuery({
    queryKey: [...opportunityKeys.all, 'allCloses'] as const,
    queryFn: () => opportunityApi.getAllCloses(),
    staleTime: 10 * 60 * 1000,
  });
}
